import api from './api';

const bookingService = {
  getBookings: (params) => api.get('/bookings', { params }),
  getBooking: (id) => api.get(`/bookings/${id}`),

  // Tenant <-> host booking chat (durable, marketplace-backed). `since` is an
  // ISO timestamp to fetch only newer messages while polling.
  getMessages: (id, since) =>
    api.get(`/bookings/${id}/messages`, { params: since ? { since } : {} }),
  sendMessage: (id, text) => api.post(`/bookings/${id}/messages`, { text }),
  createBooking: (data) => api.post('/bookings', data),
  updateBooking: (id, data) => api.put(`/bookings/${id}`, data),

  // Booking flow
  calculatePricing: (data) => api.post('/bookings/calculate-pricing', data),
  confirmBooking: (id) => api.post(`/bookings/${id}/confirm`),
  cancelBooking: (id, reason) => api.post(`/bookings/${id}/cancel`, { reason }),

  // Host approval
  approveBooking: (id, notes) => api.post(`/bookings/${id}/approve`, { notes }),
  // Backend models rejection as a cancellation with reason.
  rejectBooking: (id, reason) => api.post(`/bookings/${id}/cancel`, { reason }),

  // Negotiation offers
  listOffers: (bookingId) => api.get(`/bookings/${bookingId}/offers`),
  proposeOffer: (bookingId, data) => api.post(`/bookings/${bookingId}/offers`, data),
  acceptOffer: (bookingId, offerId) =>
    api.post(`/bookings/${bookingId}/offers/${offerId}/accept`),
  rejectOffer: (bookingId, offerId) =>
    api.post(`/bookings/${bookingId}/offers/${offerId}/reject`),

  // Contract
  listContracts: (params = {}) => api.get('/contracts', { params }),
  getContract: (id) => api.get(`/contracts/${id}`),
  getContractForBooking: (bookingId) => api.get(`/contracts/booking/${bookingId}`),
  generateContract: (bookingId) => api.post('/contracts', { booking_id: bookingId }),

  // Open the signature window on a DRAFT contract. Host-only; once
  // called, the contract moves to PENDING_SIGNATURES and either party
  // can sign (click-wrap or Aadhaar eSign). Use before this point for
  // the preview-and-confirm UX.
  sendContractForSignature: (contractId) =>
    api.post(`/contracts/${contractId}/send-for-signature`),

  // Stream the contract PDF as a blob — bearer-token auth gated.
  // Used by the in-app preview modal (an <iframe> can't carry the
  // JWT, so we fetch + create an object URL).
  downloadContractPdf: (contractId) =>
    api.get(`/contracts/${contractId}/pdf`, { responseType: 'blob' }),

  // Full signature audit trail for a contract — one row per sign
  // event with provider (click-wrap / wallet / Aadhaar eSign), IP,
  // user-agent, PDF hash, signed_at. Drives the Audit Trail tab.
  listContractSignatures: (contractId) =>
    api.get(`/contracts/${contractId}/signatures`),

  // eMudhra Aadhaar eSign — in-app OTP flow. The Aadhaar number is
  // sent on init and immediately forgotten server-side (UIDAI handles
  // the OTP delivery). The frontend stores `request_id` between the
  // two calls only — never the Aadhaar itself.
  signEmudhraInit: (contractId, { signerAadhaar, signerName } = {}) =>
    api.post(`/contracts/${contractId}/sign-emudhra/init`, {
      signer_aadhaar: signerAadhaar,
      signer_name: signerName,
    }),
  signEmudhraVerify: (contractId, { requestId, otp } = {}) =>
    api.post(`/contracts/${contractId}/sign-emudhra/verify`, {
      request_id: requestId,
      otp,
    }),
  signContract: (contractId, body) => api.post(`/contracts/${contractId}/sign`, body),
  // EIP-712 typed-data (Phase 5 wallet signing). Wallet clients
  // consume the response directly via `eth_signTypedData_v4`.
  getContractTypedData: (contractId) => api.get(`/contracts/${contractId}/typed-data`),
  // Cryptographic audit: recompute the content hash + re-verify
  // each stored wallet signature. Admin-facing; tenants/hosts can
  // also see their own contracts.
  verifyContractSignatures: (contractId) =>
    api.get(`/contracts/${contractId}/verify-signatures`),

  // Host-scoped contract defaults. GET returns a NULL-filled shell
  // when the host has never saved overrides; PUT upserts the full
  // document (``custom_clauses`` is replaced wholesale). The path
  // matches the gateway's existing ``host-*`` flat-prefix pattern;
  // the host identity is resolved from the JWT server-side so no
  // ``/me/`` segment is needed in the URL.
  getMyContractDefaults: () => api.get('/host-contract-defaults'),
  saveMyContractDefaults: (body) => api.put('/host-contract-defaults', body),

  // Contract templates — hosts compose the narrative body of the
  // contract. Platform templates are read-only; hosts fork to edit.
  listContractTemplates: () => api.get('/contract-templates'),
  getContractTemplate: (id) => api.get(`/contract-templates/${id}`),
  createContractTemplate: (body) => api.post('/contract-templates', body),
  forkContractTemplate: (id) => api.post(`/contract-templates/${id}/fork`),
  updateContractTemplate: (id, body) => api.put(`/contract-templates/${id}`, body),
  deleteContractTemplate: (id) => api.delete(`/contract-templates/${id}`),
  previewContractTemplate: (id, body = {}) =>
    api.post(`/contract-templates/${id}/preview`, body),
  // Word (.docx) exports — ``responseType: 'blob'`` so axios hands
  // us the binary instead of trying to JSON-parse the .docx ZIP.
  previewContractTemplateDocx: (id) =>
    api.get(`/contract-templates/${id}/preview.docx`, { responseType: 'blob' }),
  downloadContractDocx: (contractId) =>
    api.get(`/contracts/${contractId}/download.docx`, { responseType: 'blob' }),

  // Admin template review queue + approve/reject decision.
  listTemplateReviewQueue: () => api.get('/contract-templates/admin/review-queue'),
  reviewContractTemplate: (id, body) =>
    api.post(`/contract-templates/${id}/review`, body),

  // Amendments — propose / approve / reject / withdraw, plus list.
  listAmendments: (contractId) =>
    api.get(`/contracts/${contractId}/amendments`),
  proposeAmendment: (contractId, body) =>
    api.post(`/contracts/${contractId}/amendments`, body),
  approveAmendment: (contractId, amendmentId, body = {}) =>
    api.post(`/contracts/${contractId}/amendments/${amendmentId}/approve`, body),
  rejectAmendment: (contractId, amendmentId, reason) =>
    api.post(`/contracts/${contractId}/amendments/${amendmentId}/reject`, { reason }),
  withdrawAmendment: (contractId, amendmentId, body = {}) =>
    api.post(`/contracts/${contractId}/amendments/${amendmentId}/withdraw`, body),

  // Cancel / renew on the contract surface. Termination flips contract
  // status to TERMINATED after an explicit acknowledgment; renewal
  // re-uses the booking extend endpoint since contract validity is
  // derived from booking dates.
  terminateContract: (contractId, { reason, reasonCode, effectiveDate, acknowledgment }) =>
    api.post(`/contracts/${contractId}/terminate`, {
      reason,
      reason_code: reasonCode,
      effective_date: effectiveDate || null,
      acknowledgment: !!acknowledgment,
    }),
  renewContract: (bookingId, { months, notes } = {}) =>
    api.post(`/bookings/${bookingId}/extend`, { months, notes }),

  // Communications attached to a contract (messages, amendment notifications).
  listCommunications: (contractId) =>
    api.get(`/contracts/${contractId}/communications`),
  sendCommunication: (contractId, body) =>
    api.post(`/contracts/${contractId}/communications`, body),
  // Two-step upload: POST files → returns descriptors → include in sendCommunication.
  uploadCommunicationAttachments: (contractId, formData) =>
    api.post(`/contracts/${contractId}/communications/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  // Invoices (proxied from billing-service) + derived monthly payment schedule.
  listContractInvoices: (contractId) =>
    api.get(`/contracts/${contractId}/invoices`),
  getContractPaymentSchedule: (contractId) =>
    api.get(`/contracts/${contractId}/payment-schedule`),

  // Payment. Path is /booking-payments to avoid colliding with billing-service's
  // /payments for invoice payments. `confirmPaymentIntent` accepts the Razorpay
  // callback triplet (order_id, payment_id, signature) or an empty body for the
  // stub provider.
  getPaymentConfig: () => api.get('/booking-payments/config'),
  createPaymentIntent: (bookingId) => api.post('/booking-payments/intent', { booking_id: bookingId }),
  confirmPaymentIntent: (intentId, rzpCallback = {}) =>
    api.post(`/booking-payments/${intentId}/confirm`, rzpCallback),
  listPaymentIntents: (bookingId) =>
    api.get('/booking-payments', { params: { booking_id: bookingId } }),
  refundPaymentIntent: (intentId, { amount, reason } = {}) =>
    api.post(`/booking-payments/${intentId}/refund`, { amount, reason }),

  // Host-granted discount on a specific booking. Pass either an absolute
  // `amount` (INR) or a `percent` (0–100); backend stores both so the UI
  // can show "10% OFF · ₹500". `amount: 0` with no percent clears it.
  applyDiscount: (bookingId, { amount, percent, code, reason } = {}) =>
    api.post(`/bookings/${bookingId}/discount`, { amount, percent, code, reason }),

  // Extend a CONFIRMED/ACTIVE booking's end_date by N calendar months.
  // Pro-rata recomputes subtotal + total server-side. Caller must be
  // host, tenant, or admin of the booking.
  extendBooking: (bookingId, { months, notes } = {}) =>
    api.post(`/bookings/${bookingId}/extend`, { months, notes }),

  // Timeline
  getTimeline: (id) => api.get(`/bookings/${id}/timeline`),

  // Stats
  getBookingStats: (params) => api.get('/bookings/stats', { params }),
};

export default bookingService;
